import {
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  use,
  useMemo,
} from "react";
import { Handle, NodeProps, Position } from "reactflow";
import { useStore } from "../App/store";
import "./NodeItem.css";
import { CiSquarePlus, CiSquareMinus } from "react-icons/ci";
import { BsCheckCircleFill, BsFillInfoCircleFill } from "react-icons/bs";
import { NodeData, nodeType } from "../types/types";
import { showQuickLook } from "../utils/ShowQuickLook";
import { Image } from "primereact/image";
import { OverlayPanel } from "primereact/overlaypanel";
import ReactMarkdown from "react-markdown";
import { redirect } from "react-router-dom";
import { convertHtmlToMarkdown } from "../Helpers/NodeHelpers";
import { RiCheckboxCircleFill } from "react-icons/ri";
import { BiTask } from "react-icons/bi";
import { LuGoal, LuCheck } from "react-icons/lu";
import { TbProgressCheck } from "react-icons/tb";
import { MdOutlineAddTask, MdOutlineBugReport } from "react-icons/md";
import { FaCheckSquare, FaHandshake } from "react-icons/fa";
import { IoWarningOutline, IoFlag } from "react-icons/io5";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { SpeedDial } from "primereact/speeddial";
import { MenuItem } from "primereact/menuitem";
import { Tooltip } from "primereact/tooltip";
import { Menu } from "primereact/menu";
import UnsplashImagePicker from "./unsplashImagePicker";
import EmojiPickerPopup from "./EmojiPickerPopup";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import KanbanCard from "./KanbanCard";
import ResourceDialog from "./ResourceDialog";
import RiskDialog from "./RiskDialog";
import DecisionDialog from "./DecisionDialog";
import AddressCard from "./AddressCard";
import {
  MdPerson,
  MdLocationOn,
  MdWarning,
  MdCheckCircle,
  MdAccountTree,
  MdRemoveCircleOutline,
  MdImage,
} from "react-icons/md";

function NodeItem({
  id,
  data,
  isConnectable,
  selected,
}: NodeProps<NodeData> & { selected: boolean }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const presentationOrderInputRef = useRef<HTMLInputElement>(null);
  const priorityInputRef = useRef<HTMLInputElement>(null);
  const valueInputRef = useRef<HTMLInputElement>(null);
  const op = useRef<OverlayPanel>(null);
  const setSelectedNodeId = useStore((state) => state.setSelectedNodeId);
  const setSelectedNode = useStore((state) => state.setSelectedNode);
  const setShowNotesModal = useStore((state) => state.setShowNotesModal);
  const setCurrentNotes = useStore((state) => state.setCurrentNotes);
  const setCurrentLinks = useStore((state) => state.setCurrentLinks);
  const setCurrentNodeLabel = useStore((state) => state.setCurrentNodeLabel);
  const [showDetails, setShowDetails] = useState(false);
  const isNodeSelected = useStore((state) => state.selectedNodeId) === id;
  const nodes = useStore((state) => state.nodes);
  const [isTask, setIsTask] = useState<boolean>(data.task?.title?.length > 0);
  const [isHovered, setIsHovered] = useState<boolean>(false);
  const [showSpeedDial, setShowSpeedDial] = useState<boolean>(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [showTaskDialog, setShowTaskDialog] = useState(false);
  const [showResourceDialog, setShowResourceDialog] = useState(false);
  const [showAddressCard, setShowAddressCard] = useState(false);
  const [showRiskDialog, setShowRiskDialog] = useState(false);
  const [showDecisionDialog, setShowDecisionDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [showImageMenu, setShowImageMenu] = useState(false);
  const [showUnsplashPicker, setShowUnsplashPicker] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const imageMenuRef = useRef<Menu>(null);
  const updateNodeData = useStore((state) => state.updateNodeData);
  const showToast = useStore((state) => state.showToast);

  // Helper function to check if node is from MS365
  const isMS365Node = useMemo(() => {
    return data.links?.some((link) => link.title === "MS365 Source") || false;
  }, [data.links]);

  // Helper function to get MS365 link
  const getMS365Link = () => {
    return data.links?.find((link) => link.title === "MS365 Source");
  };

  // Helper function to get icon component from node type
  const getIconFromType = (
    type: string | undefined
  ): React.ReactNode | null => {
    if (!type) return null;
    switch (type) {
      case "task":
        // Show status-specific icon for tasks
        if (data.task?.status?.toLowerCase() === "done") {
          return <LuCheck size={16} />;
        } else if (
          data.task?.status?.toLowerCase() === "doing" ||
          data.task?.status?.toLowerCase() === "inprogress"
        ) {
          return <TbProgressCheck size={16} />;
        } else if (data.task?.status?.toLowerCase() === "testing") {
          return <MdOutlineBugReport size={16} />;
        } else if (data.task?.status?.toLowerCase() === "impediment") {
          return <IoWarningOutline size={16} />;
        } else {
          return <MdOutlineAddTask size={16} />;
        }
      case "resource":
        return <MdPerson size={16} />;
      case "location":
        return <MdLocationOn size={16} />;
      case "risk":
        return <MdWarning size={16} />;
      case "decision":
        return <FaHandshake size={16} />;
      case "image":
        // Show actual image if available, otherwise show image icon
        if (data.icon && data.icon.length > 0) {
          // Check if it's a URL or emoji
          if (
            /^https?:\/\//.test(data.icon) ||
            data.icon.startsWith("data:image/")
          ) {
            return (
              <img
                src={data.icon}
                alt="node-icon"
                style={{
                  width: "24px",
                  height: "24px",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            );
          } else {
            // Emoji or text
            return (
              <span
                style={{
                  fontSize: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                }}
              >
                {data.icon}
              </span>
            );
          }
        }
        return <MdImage size={16} />;
      default:
        return null;
    }
  };

  // Helper function to get tooltip content
  const getTooltipContent = (): string => {
    if (!data.type) return "";

    const typeLabel = data.type.charAt(0).toUpperCase() + data.type.slice(1);

    if (data.type === "task" && data.task?.status) {
      return `${typeLabel}\n${data.task.status}\n(doubleclick to edit)`;
    }

    if (data.type === "image") {
      return `${typeLabel}\n(doubleclick to view)`;
    }

    return `${typeLabel}\n(doubleclick to edit)`;
  };

  const [selectedSpeedDialIcon, setSelectedSpeedDialIcon] =
    useState<React.ReactNode | null>(getIconFromType(data.type));

  // Update icon when task status changes
  useEffect(() => {
    if (data.type === "task") {
      setSelectedSpeedDialIcon(getIconFromType(data.type));
    }
  }, [data.task?.status]);

  // Update icon when type or image changes
  useEffect(() => {
    setSelectedSpeedDialIcon(getIconFromType(data.type));
  }, [data.icon, data.type]);

  // Add tooltip to main SpeedDial button only
  useEffect(() => {
    if (selectedSpeedDialIcon) {
      const speedDialButton = wrapperRef.current
        ?.closest(".react-flow__node")
        ?.querySelector(".node-speeddial .p-speeddial-button");

      if (speedDialButton) {
        speedDialButton.setAttribute("data-pr-tooltip", getTooltipContent());
        speedDialButton.setAttribute("data-pr-position", "top");
        speedDialButton.setAttribute("data-pr-at", "center top");
      }
    }
  }, [selectedSpeedDialIcon, data.type, data.task?.status]);

  // Add tooltips to action buttons
  useEffect(() => {
    // Use a small delay to ensure action buttons are rendered
    const timeoutId = setTimeout(() => {
      const actionButtons = wrapperRef.current
        ?.closest(".react-flow__node")
        ?.querySelectorAll(".node-speeddial .p-speeddial-action");

      if (actionButtons && actionButtons.length > 0) {
        const labels = [
          "Clear Type",
          "Task",
          "Resource",
          "Location",
          "Risk",
          "Decision",
          "Image",
        ];
        actionButtons.forEach((button, index) => {
          if (labels[index]) {
            button.setAttribute("data-pr-tooltip", labels[index]);
            button.setAttribute("data-pr-position", "top");
            button.setAttribute("data-pr-at", "center top");
          }
        });
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isHovered, selectedSpeedDialIcon]);

  // Add tooltip to MS365 icon
  useEffect(() => {
    if (isMS365Node) {
      const ms365Icon = wrapperRef.current
        ?.closest(".react-flow__node")
        ?.querySelector(".node-ms365-icon");

      if (ms365Icon) {
        ms365Icon.setAttribute(
          "data-pr-tooltip",
          "MS365\n(doubleclick to open)"
        );
        ms365Icon.setAttribute("data-pr-position", "top");
        ms365Icon.setAttribute("data-pr-at", "center top");
      }
    }
  }, [isMS365Node, data.icon]);

  // Handle delayed speed dial appearance
  useEffect(() => {
    if (isHovered) {
      // Set a delay before showing the speed dial
      hoverTimeoutRef.current = setTimeout(() => {
        setShowSpeedDial(true);
      }, 300); // 300ms delay
    } else {
      // Clear timeout if mouse leaves before delay completes
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      // Hide speed dial immediately when mouse leaves
      setShowSpeedDial(false);
    }

    // Cleanup on unmount
    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isHovered]);

  // Handler for changing node type with task confirmation
  const handleTypeChange = (
    newType: nodeType | null,
    newIcon: React.ReactNode | null
  ) => {
    // If currently a task and changing to a different type, show confirmation
    if (data.type === "task" && newType !== "task" && isTask) {
      setConfirmDialogVisible(true);
      // Store the pending change
      (window as any).pendingTypeChange = { newType, newIcon };
    } else {
      // Direct change - no confirmation needed
      setSelectedSpeedDialIcon(newIcon);
      if (newType === null) {
        // Clear the type
        updateNodeData(id, { type: undefined });
      } else {
        updateNodeData(id, { type: newType });
        if (newType === "task" && !isTask) {
          updateNodeTask(id, true, data.label);
          setIsTask(true);
        }
      }
    }
  };

  // SpeedDial items for project planning
  const speedDialItems: MenuItem[] = [
    {
      icon: <MdRemoveCircleOutline size={20} />,
      command: () => {
        console.log("Clear type clicked");
        handleTypeChange(null, null);
      },
    },
    {
      icon: <MdOutlineAddTask size={20} />,
      command: () => {
        console.log("Task clicked");
        handleTypeChange("task", <MdOutlineAddTask size={16} />);
      },
    },
    {
      icon: <MdPerson size={20} />,
      command: () => {
        console.log("Resource clicked");
        handleTypeChange("resource", <MdPerson size={16} />);
      },
    },
    {
      icon: <MdLocationOn size={20} />,
      command: () => {
        console.log("Location clicked");
        handleTypeChange("location", <MdLocationOn size={16} />);
      },
    },
    {
      icon: <MdWarning size={20} />,
      command: () => {
        console.log("Risk clicked");
        handleTypeChange("risk", <MdWarning size={16} />);
      },
    },
    {
      icon: <FaHandshake size={20} />,
      command: () => {
        console.log("Decision clicked");
        handleTypeChange("decision", <FaHandshake size={16} />);
      },
    },
    {
      icon: <MdImage size={20} />,
      command: () => {
        console.log("Image clicked");
        handleTypeChange("image", <MdImage size={16} />);
      },
    },
  ];

  // Check if node is disabled (from Focus Filtering)
  const currentNode = useMemo(
    () => nodes.find((node) => node.id === id),
    [nodes, id]
  );
  const isDisabled = useMemo(
    () => currentNode?.draggable === false,
    [currentNode?.draggable]
  );
  const isDisabledByFilter = useMemo(
    () => (currentNode?.data as any)?.isDisabledByFilter === true,
    [currentNode?.data]
  );

  useLayoutEffect(() => {
    if (inputRef.current && wrapperRef.current) {
      wrapperRef.current.style.width = inputRef.current.style.width;
    }
  }, [inputRef.current?.style.width]);

  const updateNodeLabel = useStore((state) => state.updateNodeLabel);
  const updateNodeTask = useStore((state) => state.updateNodeTask);
  const updatePresentationOrder = useStore(
    (state) => state.updatePresentationOrder
  );
  const updatePriority = useStore((state) => state.updatePriority);
  const updateValue = useStore((state) => state.updateValue);
  const focusValue = useStore((state) => state.focusValue);
  const cursorPositionRef = useRef<number | null>(null);
  const isPresentationMode = useStore((state) => state.isPresentationMode);
  const presentWithShowHideButtons = useStore(
    (state) => state.presentWithShowHideButtons
  );

  // Handler for task updates from KanbanCard
  const handleTaskUpdate = (
    taskId: string | number,
    title: string,
    nodeTitle: string,
    status: string,
    summary: string,
    priority?: string,
    tags?: string,
    assignee?: string,
    estimate?: number,
    rankId?: number,
    color?: string,
    className?: string,
    startDate?: Date,
    endDate?: Date
  ) => {
    const taskData = {
      label: title, // Update the node label to match the task title
      task: {
        id: taskId,
        nodeTitle: title, // Use title for nodeTitle as well
        title: title,
        status: status,
        summary: summary,
        priority: priority,
        tags: tags,
        assignee: assignee,
        estimate: estimate,
        rankId: rankId,
        color: color,
        className: className,
        startDate: startDate,
        endDate: endDate,
      },
    };
    updateNodeData(id, taskData);
    setIsTask(true);
  };

  // Handler for task deletion
  const handleTaskDelete = (taskId: string | number) => {
    updateNodeTask(id, false, "");
    setIsTask(false);
  };

  // Handler for image URL update
  const handleImageUrlSave = () => {
    if (imageUrl.trim()) {
      updateNodeData(id, { icon: imageUrl.trim(), type: "image" });
      setSelectedSpeedDialIcon(getIconFromType("image"));
    }
    setShowImageDialog(false);
    setImageUrl("");
  };

  // Handler for resource updates
  const handleResourceSave = (resourceData: {
    id?: string;
    name: string;
    role: string;
    department: string;
    email: string;
    phone: string;
    availability: number;
    skills: string;
    notes: string;
  }) => {
    updateNodeData(id, {
      label: resourceData.name, // Update node label to match resource name
      resource: resourceData,
    });
  };

  // Handler for resource deletion
  const handleResourceDelete = () => {
    updateNodeData(id, { resource: undefined });
    setShowResourceDialog(false);
  };

  // Handler for address/location updates
  const handleAddUpdateAddress = (
    addressId: string,
    nodeTitle: string,
    name: string,
    street: string,
    zip_code: string,
    city: string,
    Phone: string,
    email: string,
    vat: string,
    employees: string,
    notes: string,
    latitude?: number,
    longitude?: number
  ) => {
    updateNodeData(id, {
      label: name, // Update node label to match address name
      address: {
        id: addressId || id,
        nodeTitle,
        name,
        street,
        zip_code,
        city,
        Phone,
        email,
        vat,
        employees,
        notes,
        latitude,
        longitude,
      },
    });
  };

  // Handler for address deletion
  const handleDeleteAddress = (addressId: string) => {
    updateNodeData(id, { address: undefined });
    setShowAddressCard(false);
  };

  // Handler for risk updates
  const handleRiskSave = (riskData: {
    id?: string;
    name: string;
    description: string;
    category: string;
    impact: string;
    likelihood: string;
    severity: string;
    mitigation: string;
    owner: string;
    status: string;
    identifiedDate?: Date | string;
    reviewDate?: Date | string;
  }) => {
    updateNodeData(id, {
      label: riskData.name, // Update node label to match risk name
      risk: riskData,
    });
  };

  // Handler for risk deletion
  const handleRiskDelete = () => {
    updateNodeData(id, { risk: undefined });
    setShowRiskDialog(false);
  };

  // Handler for decision save
  const handleDecisionSave = (decisionData: {
    id?: string;
    name: string;
    description: string;
    category: string;
    status: string;
    decisionMaker: string;
    stakeholders: string;
    criteria: string;
    alternatives: string;
    selectedOption: string;
    rationale: string;
    implications: string;
    decisionDate?: Date | string;
    reviewDate?: Date | string;
  }) => {
    updateNodeData(id, {
      label: decisionData.name, // Update node label to match decision name
      decision: decisionData,
    });
  };

  // Handler for decision deletion
  const handleDecisionDelete = () => {
    updateNodeData(id, { decision: undefined });
    setShowDecisionDialog(false);
  };

  // Handler for image menu options
  const handleImageFromUnsplash = () => {
    setShowUnsplashPicker(true);
  };

  const handleImageFromUrl = () => {
    setShowImageDialog(true);
  };

  const handleImageFromEmoji = () => {
    setShowEmojiPicker(true);
  };

  const handleEmojiSelect = (emojiData: any) => {
    updateNodeData(id, { icon: emojiData.emoji });
    setShowEmojiPicker(false);
  };

  const handleRemoveImage = () => {
    updateNodeData(id, { icon: "" });
  };

  const imageMenuItems: MenuItem[] = [
    {
      label: "From Unsplash",
      icon: "pi pi-images",
      command: handleImageFromUnsplash,
    },
    {
      label: "From URL",
      icon: "pi pi-link",
      command: handleImageFromUrl,
    },
    {
      label: "Emoji",
      icon: "pi pi-face-smile",
      command: handleImageFromEmoji,
    },
    {
      separator: true,
    },
    {
      label: "Remove Image",
      icon: "pi pi-times",
      command: handleRemoveImage,
      disabled: !data.icon || data.icon.length === 0,
    },
  ];

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    const key = event.currentTarget.getAttribute("node-identifier");
    setSelectedNodeId(key);
    event.currentTarget.select();
  };

  const handleBlur = () => {
    setSelectedNodeId(null);
  };

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    const key = event.currentTarget.getAttribute("node-identifier");
    setSelectedNodeId(key);
  };

  const handleDetailsClick = (event: React.MouseEvent<Element>): void => {
    event.stopPropagation();
    setShowDetails((prevShowDetails) => !prevShowDetails);
  };

  // Removed auto-focus to allow ReactFlow's built-in keyboard navigation to work
  // useEffect(() => {
  //   setTimeout(() => {
  //     inputRef.current?.focus({ preventScroll: true });
  //   }, 1);
  // }, []);

  useLayoutEffect(() => {
    // console.log("focusValue", focusValue);
    if (inputRef.current) {
      const minWidth = 50; // Minimum width in pixels
      const padding = 10; // Padding to add some space around the text

      inputRef.current.setAttribute("spellcheck", "false");

      // Create a canvas element to measure the text width
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      if (context) {
        context.font = getComputedStyle(inputRef.current).font;
        const textWidth = context.measureText(inputRef.current.value).width;
        inputRef.current.style.width = `${Math.max(
          minWidth,
          textWidth + padding
        )}px`;
        if (dragRef.current) {
          dragRef.current.style.width = `${Math.max(
            minWidth,
            textWidth + padding
          )}px`;
        }
      }
    }
  }, [data.label]);

  const handleChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    updateNodeLabel(id, evt.target.value);
  };

  const handleKeyDown = (evt: React.KeyboardEvent<HTMLInputElement>) => {
    // Cmd+Enter (Mac) or Ctrl+Enter to create child nodes
    if (evt.key === "Enter" && (evt.metaKey || evt.ctrlKey)) {
      evt.preventDefault();

      // Get current state from store
      const store = useStore.getState();
      const tabModeActive = store.tabModeActive;
      const tabModeParentId = store.tabModeParentId;

      // Determine the parent node for tab mode
      let parentNodeId = id;

      if (tabModeActive && tabModeParentId) {
        // Continue with existing parent
        parentNodeId = tabModeParentId;
      } else {
        // Start new tab mode with current node as parent
        store.setTabModeActive(true);
        store.setTabModeParentId(id);
      }

      // Create new child node
      const newNodeId = store.addChildNodeInTabMode(parentNodeId);

      // Redistribute all children evenly
      store.redistributeChildNodes(parentNodeId);

      // Focus the new node after a brief delay
      setTimeout(() => {
        const newInput = document.querySelector(
          `[node-identifier="${newNodeId}"]`
        ) as HTMLInputElement;
        if (newInput) {
          newInput.focus({ preventScroll: true });
          newInput.select();
        }
      }, 50);
    } else if (evt.key === "Escape") {
      // Stop Tab mode on ESC key
      const store = useStore.getState();
      if (store.tabModeActive) {
        evt.preventDefault();
        store.setTabModeActive(false);
        store.setTabModeParentId(null);
      }
    }
  };

  const acceptRemoveTask = () => {
    setConfirmDialogVisible(false);
    updateNodeTask(id, false, "");
    setIsTask(false);

    // If there's a pending type change, execute it
    const pendingChange = (window as any).pendingTypeChange;
    if (pendingChange) {
      setSelectedSpeedDialIcon(pendingChange.newIcon);
      updateNodeData(id, { type: pendingChange.newType });
      (window as any).pendingTypeChange = null;
    }
  };

  const handlePresentationOrder = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = parseInt(evt.target.value, 10);
    const value = isNaN(parsedValue) ? "" : parsedValue.toString();
    updatePresentationOrder(id, value);
  };

  const handlePriorityChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const value = evt.target.value;
    const isValid =
      value === "" || /^[A-Za-z]\d*$/.test(value) || /^\d+$/.test(value);
    if (isValid) {
      updatePriority(id, value);
    }
  };

  const handleValueChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = evt.target.value;
    updateValue(id, parsedValue);
  };

  const handleValueResult = (evt: React.ChangeEvent<HTMLInputElement>) => {
    cursorPositionRef.current = evt.target.selectionStart;
    const parsedValue = evt.target.value;
    const floatValue = parseFloat(parsedValue);
    if (!isNaN(floatValue)) {
      updateValue(id, floatValue.toString());
    } else {
      updateValue(id, "");
    }
  };

  useEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.selectionStart = cursorPositionRef.current;
      inputRef.current.selectionEnd = cursorPositionRef.current;
    }
  }, [data.label]);

  const hideChildNodes = useStore((state) => state.hideChildNodes);
  const showChildNodes = useStore((state) => state.showChildNodes);
  const [displayedNodeValue, setDisplayedNodeValue] = useState<
    React.ReactNode | undefined
  >();
  const doRollup = useStore((state) => state.doRollup);
  const [isNegativeValue, setIsNegativeValue] = useState(false);
  const activateShowValues = useStore((state) => state.showValues);
  const updateValues = useStore((state) => state.updateValues);

  const getValuesOutput = (values: string[], isRollup: boolean) => {
    return (
      <div className={`rollup-display ${isNegativeValue ? "negative" : ""}`}>
        <div className="rollup-container">
          {values.map((value, index) => {
            const hasSigma = value.includes("Σ");
            return (
              <div
                key={index}
                className={isRollup ? "rollup-value" : "regular-value"}
                style={hasSigma ? { fontWeight: "bold" } : {}}
              >
                {value}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const displayValues: string[] = [];
    if (doRollup) {
      const rollupValues = data.rollupValues || [];
      let hasNonZeroValue = false;
      if (rollupValues) {
        rollupValues.forEach((rollup) => {
          const val = rollup?.value;
          // Check if value is meaningful (not zero, null, empty, etc.)
          const numericValue = typeof val === "string" ? parseFloat(val) : val;
          const isZero =
            val === null ||
            val === undefined ||
            val === "" ||
            val === "0" ||
            (typeof numericValue === "number" && numericValue === 0);
          // Also check if it's a string like "0 Σ" by parsing the numeric part
          const isZeroValue = isZero;

          if (rollup?.title && !isZeroValue) {
            displayValues.push(rollup.title + ": " + rollup.value);
            hasNonZeroValue = true;
          }
        });
      }
      const valuesOutput: React.ReactNode | undefined =
        displayValues.length > 0 && hasNonZeroValue
          ? getValuesOutput(displayValues, true)
          : undefined;
      setDisplayedNodeValue(valuesOutput);
    } else {
      const values = data.values || [];
      let hasNonZeroValue = false;
      if (values) {
        values.forEach((value) => {
          const val = value?.value;
          // Check if value is meaningful
          const numericValue = typeof val === "string" ? parseFloat(val) : val;
          const isZeroValue =
            !val ||
            val === "0" ||
            (typeof numericValue === "number" && numericValue === 0);

          if (value?.title && val && val.length > 0 && !isZeroValue) {
            displayValues.push(value.title + ": " + value.value);
            hasNonZeroValue = true;
          }
        });
      }
      const valuesOutput: React.ReactNode | undefined =
        displayValues.length > 0 && hasNonZeroValue
          ? getValuesOutput(displayValues, false)
          : undefined;
      setDisplayedNodeValue(valuesOutput);
    }
  }, [activateShowValues, data, doRollup]);

  useEffect(() => {
    //console.log("Node data updated:", data);
  }, [data]);

  const hasHiddenChildren = useMemo(
    () => nodes.some((node) => node.parentId === id && node.hidden),
    [nodes, id]
  );
  const hasVisibleChildren = useMemo(
    () => nodes.some((node) => node.parentId === id && !node.hidden),
    [nodes, id]
  );

  const handleHideShowClick = (event: React.MouseEvent) => {
    const showAllLevels = event.shiftKey;
    if (hasHiddenChildren) {
      showChildNodes(id, showAllLevels);
    } else {
      hideChildNodes(id);
      setTimeout(() => {
        document.getSelection()?.removeAllRanges();
        inputRef.current?.blur();
      }, 0);
    }
  };

  return (
    <>
      <Tooltip />
      <OverlayPanel ref={op}>
        {data.icon && (
          <>
            {/^https?:\/\//.test(data.icon) ||
            data.icon.startsWith("data:image/") ? (
              <img
                src={data.icon}
                alt="node-image"
                style={{ width: "300px" }}
              />
            ) : (
              <div
                style={{
                  fontSize: "150px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "20px",
                }}
              >
                {data.icon}
              </div>
            )}
          </>
        )}
      </OverlayPanel>
      <div
        key={id}
        onContextMenu={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onClick={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onDoubleClick={
          isDisabled
            ? (e) => {
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onKeyDown={
          isDisabled
            ? (e) => {
                // Allow arrow keys for navigation even when disabled
                if (e.key.startsWith("Arrow") || e.key === "Tab") {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onKeyUp={
          isDisabled
            ? (e) => {
                // Allow arrow keys for navigation even when disabled
                if (e.key.startsWith("Arrow") || e.key === "Tab") {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        onKeyPress={
          isDisabled
            ? (e) => {
                // Allow arrow keys for navigation even when disabled
                if (e.key.startsWith("Arrow") || e.key === "Tab") {
                  return;
                }
                e.preventDefault();
                e.stopPropagation();
              }
            : undefined
        }
        style={
          isDisabled ? { pointerEvents: "none", userSelect: "none" } : undefined
        }
      >
        {(!isPresentationMode || presentWithShowHideButtons) && (
          <div>
            {hasVisibleChildren && !hasHiddenChildren && (
              <div className="show-hide-area">
                <CiSquareMinus
                  className="show-hide-icon show-hide-icon-minus"
                  onClick={handleHideShowClick}
                />
              </div>
            )}
            {hasHiddenChildren && (
              <div className="show-hide-area">
                <CiSquarePlus
                  className="show-hide-icon show-hide-icon-plus"
                  onClick={handleHideShowClick}
                />
              </div>
            )}
          </div>
        )}

        {focusValue === "presentation" && (
          <div className="presentation-order-area">
            <input
              id="presentation-order-input-field"
              className="presentation-order-input-field"
              spellCheck="false"
              value={data.presentationOrder}
              onChange={handlePresentationOrder}
              onFocus={handleFocus}
              ref={presentationOrderInputRef}
            />
          </div>
        )}

        {focusValue === "priorities" ? (
          <div className="priority-area">
            <input
              id="priority-input-field"
              className="priority-input-field"
              spellCheck="false"
              value={data.priority}
              onChange={handlePriorityChange}
              onFocus={handleFocus}
              ref={priorityInputRef}
            />
          </div>
        ) : (
          data.priority &&
          (focusValue === "prioritiesView" || focusValue === "all") && (
            <div className="priority-display">{data.priority}</div>
          )
        )}

        {focusValue === "rollup" ? (
          <div className="rollup-area">
            <input
              id="rollup-input-field"
              className="rollup-input-field"
              spellCheck="false"
              value={data.value}
              onChange={handleValueChange}
              onBlur={handleValueResult}
              onFocus={handleFocus}
              ref={valueInputRef}
            />
          </div>
        ) : (
          activateShowValues && <div>{displayedNodeValue}</div>
        )}

        {/* <div className="nodeitem-info-area"> */}
        {/* {data.complete && (focusValue === "done" || focusValue === "all") && ( */}
        {/* <div className="node-completed-indicator">
        <BsCheckCircleFill color="#a8ac5e" />
      </div> */}

        <ConfirmDialog
          id="task-confirmation-dialog"
          visible={confirmDialogVisible}
          onHide={() => {
            setConfirmDialogVisible(false);
            (window as any).pendingTypeChange = null;
          }}
          header="Confirmation"
          message="Removing the task will also remove the task title and all associated details. Are you sure you want to proceed?"
          icon="pi pi-exclamation-triangle"
          accept={acceptRemoveTask}
          reject={() => {
            setConfirmDialogVisible(false);
            (window as any).pendingTypeChange = null;
          }}
        />

        {useMemo(
          () => (
            <div
              style={{
                backgroundColor:
                  focusValue == "all" ? data.backgroundColor : "white",
              }}
              ref={dragRef}
              onClick={() => setSelectedNodeId(id)}
              className={`dragHandle node-dragHandle ${
                isNodeSelected ||
                selected ||
                document.querySelector(`[node-identifier="${id}"]`) ===
                  document.activeElement
                  ? "node-selected"
                  : ""
              } ${
                isDisabledByFilter
                  ? "draghandle-color-disabled"
                  : "draghandle-color-standard"
              }`}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
          ),
          [data.backgroundColor, focusValue, selected, id, isDisabledByFilter]
        )}

        {/* SpeedDial for project planning */}
        {(showSpeedDial || selectedSpeedDialIcon) &&
          !isDisabled &&
          !isMS365Node && (
            <div
              className="node-speeddial"
              onMouseEnter={() => !isDisabled && setIsHovered(true)}
              onClick={() => {
                setSelectedNodeId(id);
                setSelectedNode(nodes.find((node) => node.id === id));
              }}
              onDoubleClick={(e) => {
                e.stopPropagation();
                const nodeType = data.type;
                if (!nodeType) return;

                switch (nodeType) {
                  case "task":
                    setShowTaskDialog(true);
                    break;
                  case "resource":
                    setShowResourceDialog(true);
                    break;
                  case "location":
                    setShowAddressCard(true);
                    break;
                  case "risk":
                    setShowRiskDialog(true);
                    break;
                  case "decision":
                    setShowDecisionDialog(true);
                    break;
                  case "image":
                    // Show image/emoji in overlay
                    if (data.icon && data.icon.length > 0) {
                      op.current?.toggle(e);
                    } else {
                      showToast(
                        "info",
                        "Select Image",
                        "Right-click and choose an option from the Image menu"
                      );
                    }
                    break;
                }
              }}
              style={{
                position: "absolute",
                left: "-36px",
                top: "-2px",
                zIndex: 9999,
                pointerEvents: "auto",
              }}
            >
              <SpeedDial
                model={speedDialItems}
                radius={55}
                type="semi-circle"
                direction="left"
                style={{
                  width: "1.5rem",
                  height: "0.5rem",
                }}
                buttonStyle={{
                  width: "1.5rem",
                  height: "0.5rem",
                  backgroundColor: selectedSpeedDialIcon
                    ? "rgb(18, 102, 86)"
                    : "rgba(18, 102, 86, 0.5)",
                  color: "white",
                  borderRadius: "50%",
                }}
                showIcon={selectedSpeedDialIcon || "pi pi-plus"}
                hideIcon={selectedSpeedDialIcon || "pi pi-times"}
              />
            </div>
          )}

        {/* MS365 icon for MS365 nodes */}
        {isMS365Node && data.icon && (
          <div
            className="node-ms365-icon"
            style={{
              position: "absolute",
              left: "-36px",
              top: "-2px",
              zIndex: 9999,
              width: "1.5rem",
              height: "1.5rem",
              borderRadius: "50%",
              backgroundColor: "rgb(18, 102, 86)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              overflow: "hidden",
            }}
            onClick={() => {
              setSelectedNodeId(id);
              setSelectedNode(nodes.find((node) => node.id === id));
            }}
            onDoubleClick={(e) => {
              e.stopPropagation();
              const ms365Link = getMS365Link();
              if (ms365Link && ms365Link.url) {
                window.open(ms365Link.url, "_blank", "noopener,noreferrer");
              }
            }}
          >
            {/^https?:\/\//.test(data.icon) ||
            data.icon.startsWith("data:image/") ? (
              <img
                src={data.icon}
                alt="ms365-icon"
                style={{
                  width: "24px",
                  height: "24px",
                  objectFit: "cover",
                  borderRadius: "50%",
                }}
              />
            ) : (
              <span style={{ fontSize: "16px" }}>{data.icon}</span>
            )}
          </div>
        )}

        <div
          ref={wrapperRef}
          className="inputWrapper"
          style={{
            opacity: isDisabledByFilter ? 0.4 : 1,
            backgroundColor: isDisabledByFilter
              ? "rgb(255, 255, 255)"
              : "transparent",
          }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div
            className="inputArea"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {false && data.icon && data.icon.length > 0 ? (
              // Check if icon is a URL (http/https), a base64 string, or a file extension
              /^https?:\/\//.test(data.icon) ||
              data.icon.endsWith(".png") ||
              data.icon.endsWith(".jpg") ||
              data.icon.endsWith(".jpeg") ||
              data.icon.startsWith("data:image/") ? (
                <div
                  className="custom-node-icon dragHandle"
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  style={{
                    top: "-11px",
                    left: "-30px",
                    height: "30px",
                    width: "30px",
                    borderRadius: "50%",
                    border: "1px solid rgb(18, 102, 86)",
                    backgroundColor: "gainsboro",
                    overflow: "hidden",
                  }}
                >
                  <img
                    className="image"
                    src={data.icon}
                    alt="node-icon"
                    style={{
                      height: "100%",
                      width: "100%",
                      objectFit: "cover",
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    onClick={(e) => op.current?.toggle(e)}
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      if (data.links && data.links.length > 0) {
                        const ms365Link = data.links.find(
                          (link) => link.title === "MS365 Source"
                        );
                        if (ms365Link && ms365Link.url) {
                          window.open(
                            ms365Link.url,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      }
                    }}
                  />
                  <OverlayPanel ref={op}>
                    <img src={data.icon} style={{ width: "300px" }} />
                  </OverlayPanel>
                </div>
              ) : (
                <div
                  className="custom-node-icon dragHandle"
                  style={{
                    top: "-7.6px",
                    left: "-23px",
                    height: "25px",
                    width: "25px",
                    borderRadius: "50%",
                    backgroundColor: "white",
                    border: "1px solid rgb(18, 102, 86)",
                    alignContent: "center",
                    justifyContent: "center",
                    display: "flex",
                    alignItems: "center",
                    justifyItems: "center",
                    alignSelf: "center",
                    overflow: "hidden",
                  }}
                  onMouseEnter={() => setIsHovered(true)}
                  onMouseLeave={() => setIsHovered(false)}
                  onDoubleClick={(e) => {
                    e.stopPropagation();
                    if (data.links && data.links.length > 0) {
                      const ms365Link = data.links.find(
                        (link) => link.title === "MS365 Source"
                      );
                      if (ms365Link && ms365Link.url) {
                        window.open(
                          ms365Link.url,
                          "_blank",
                          "noopener,noreferrer"
                        );
                      }
                    }
                  }}
                >
                  <div
                    className={`custom-node-icon dragHandle`}
                    style={{
                      fontSize: "1.2rem",
                      top: "0px",
                      left: "3.5px",
                      width: "100%",
                    }}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                  >
                    {data.icon}
                  </div>
                </div>
              )
            ) : (
              <></>
            )}
            {/* Notes icon - positioned inside node on the left */}
            {data.notes?.length > 0 &&
              (focusValue === "notes" || focusValue === "all") && (
                <div className="notes-indicator-left">
                  <BsFillInfoCircleFill
                    style={{
                      cursor: "pointer",
                      fontSize: "0.7rem",
                      color: "#126656",
                    }}
                    onMouseEnter={(e) => {
                      // Show details after 1 second hover
                      const target =
                        e.currentTarget as unknown as HTMLElement & {
                          __hoverTimeout?: ReturnType<typeof setTimeout>;
                        };
                      target.__hoverTimeout = setTimeout(
                        () => handleDetailsClick(e),
                        1000
                      );
                    }}
                    onMouseLeave={(e) => {
                      // Cancel timer and hide details
                      const target =
                        e.currentTarget as unknown as HTMLElement & {
                          __hoverTimeout?: ReturnType<typeof setTimeout> | null;
                        };
                      if (target.__hoverTimeout) {
                        clearTimeout(target.__hoverTimeout);
                        target.__hoverTimeout = null;
                      }
                      setShowDetails(false);
                    }}
                    onDoubleClick={(e) => {
                      if (e.shiftKey && data.links && data.links.length > 0) {
                        const ms365Link = data.links.find(
                          (link) => link.title === "MS365 Source"
                        );
                        if (ms365Link && ms365Link.url) {
                          window.open(
                            ms365Link.url,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }
                      } else {
                        setSelectedNodeId(id);
                        setSelectedNode(nodes.find((node) => node.id === id));
                        setShowNotesModal(true);
                      }
                    }}
                  />
                </div>
              )}
            <input
              id="node-input-field"
              className="node-input"
              node-identifier={id}
              spellCheck="false"
              autoComplete="off"
              style={{ color: focusValue !== "all" ? "black" : data.color }}
              value={data.label}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onClick={() => setSelectedNodeId(id)}
              ref={inputRef}
              onMouseEnter={() => setIsHovered(true)}
              onMouseLeave={() => setIsHovered(false)}
            />
            {/* Flag icon - positioned above node */}
            {data.flagged &&
              data.flagged !== "none" &&
              (focusValue === "marker" || focusValue === "all") && (
                <div className="node-icons-row">
                  <IoFlag
                    className="node-flag-icon"
                    style={{
                      cursor: "pointer",
                      fontSize: "0.8rem",
                      color:
                        data.flagged === "green"
                          ? "#8a9a5b"
                          : data.flagged === "yellow"
                          ? "#d2b773"
                          : data.flagged === "red"
                          ? "#b76e79"
                          : "#126656",
                    }}
                  />
                </div>
              )}
            {/* {(data.notes && data.notes.length > 0) || isNodeSelected ? (
            showDetails ? (
              <i
                className="pi pi-angle-down node-details"
                style={{
                  cursor: "pointer",
                  color:
                    data.notes && data.notes.length > 0
                      ? "black"
                      : undefined,
                }}
                onClick={handleDetailsClick}
              ></i>
            ) : (
              <i
                className="pi pi-angle-right node-details"
                style={{
                  cursor: "pointer",
                  color:
                    data.notes && data.notes.length > 0
                      ? "black"
                      : undefined,
                }}
                onClick={handleDetailsClick}
              ></i>
            )
          ) : null} */}
          </div>
        </div>

        <Handle
          type="target"
          position={Position.Top}
          isConnectable={isConnectable}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />

        <Handle
          type="source"
          position={Position.Top}
          isConnectable={isConnectable}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />

        <Handle
          type="source"
          position={Position.Right}
          id="cross-link"
          style={{ background: "red" }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />

        <Handle
          type="target"
          position={Position.Left}
          id="cross-link"
          style={{ background: "blue" }}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        />

        {showDetails && (
          <div className="node-details-area">
            <div className="node-details-label">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <h1
                      style={{
                        fontSize: "2em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      style={{
                        fontSize: "1.5em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3
                      style={{
                        fontSize: "1.2em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  h4: ({ node, ...props }) => (
                    <h4
                      style={{
                        fontSize: "1.1em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  p: ({ node, ...props }) => (
                    <p
                      style={{
                        fontSize: "1em",
                        marginTop: "0em",
                        marginBottom: "0.3em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "1em", // reduced from 1.2em to 1em
                        listStylePosition: "outside",
                      }}
                      {...props}
                    />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "1em", // reduced from 1.2em to 1em
                        listStylePosition: "outside",
                      }}
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => (
                    <li
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "0em", // set to 0 to minimize bullet gap
                      }}
                      {...props}
                    />
                  ),
                  input: ({ node, ...props }) =>
                    props.type === "checkbox" ? (
                      <input
                        style={{
                          fontSize: "1em",
                          margin: "0em",
                          paddingInlineStart: "0em",
                        }}
                        {...props}
                      />
                    ) : (
                      <input {...props} />
                    ),
                  table: () => null,
                  thead: () => null,
                  tbody: () => null,
                  tr: () => null,
                  th: () => null,
                  td: () => null,
                }}
              >
                {convertHtmlToMarkdown(data.notes)}
              </ReactMarkdown>
            </div>
          </div>
        )}

        {/* Notes Details */}
        {showDetails && data.notes && (
          <div className="node-details-area">
            <div className="node-details-label">
              <ReactMarkdown
                components={{
                  h1: ({ node, ...props }) => (
                    <h1
                      style={{
                        fontSize: "2em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2
                      style={{
                        fontSize: "1.5em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3
                      style={{
                        fontSize: "1.2em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  h4: ({ node, ...props }) => (
                    <h4
                      style={{
                        fontSize: "1.1em",
                        marginTop: "0em",
                        marginBottom: "0em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  p: ({ node, ...props }) => (
                    <p
                      style={{
                        fontSize: "1em",
                        marginTop: "0em",
                        marginBottom: "0.3em",
                        paddingTop: "0em",
                        paddingBottom: "0em",
                      }}
                      {...props}
                    />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "1em",
                        listStylePosition: "outside",
                      }}
                      {...props}
                    />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "1em",
                        listStylePosition: "outside",
                      }}
                      {...props}
                    />
                  ),
                  li: ({ node, ...props }) => (
                    <li
                      style={{
                        fontSize: "1em",
                        margin: "0em",
                        paddingInlineStart: "0em",
                      }}
                      {...props}
                    />
                  ),
                  input: ({ node, ...props }) =>
                    props.type === "checkbox" ? (
                      <input
                        style={{
                          fontSize: "1em",
                          margin: "0em",
                          paddingInlineStart: "0em",
                        }}
                        {...props}
                      />
                    ) : (
                      <input {...props} />
                    ),
                  table: () => null,
                  thead: () => null,
                  tbody: () => null,
                  tr: () => null,
                  th: () => null,
                  td: () => null,
                }}
              >
                {convertHtmlToMarkdown(data.notes)}
              </ReactMarkdown>
            </div>
          </div>
        )}
      </div>

      {/* Task Dialog */}
      {showTaskDialog && (
        <KanbanCard
          visible={showTaskDialog}
          nodeTask={
            data.task || {
              id: `task-${id}`,
              title: data.label,
              nodeTitle: data.label,
              status: "todo",
            }
          }
          setShowKanbanCard={setShowTaskDialog}
          handleAddUpdateTask={handleTaskUpdate}
          handleDeleteTask={handleTaskDelete}
        />
      )}

      {/* Resource Dialog */}
      {showResourceDialog && (
        <ResourceDialog
          visible={showResourceDialog}
          resourceData={
            data.resource || {
              name: data.label,
              role: "",
              department: "",
              email: "",
              phone: "",
              availability: 100,
              skills: "",
              notes: "",
            }
          }
          setShowResourceDialog={setShowResourceDialog}
          handleSaveResource={handleResourceSave}
          handleDeleteResource={
            data.resource ? handleResourceDelete : undefined
          }
        />
      )}

      {/* Address/Location Card */}
      {showAddressCard && (
        <AddressCard
          visible={showAddressCard}
          nodeAddress={
            data.address || {
              id: id,
              nodeTitle: data.label,
              name: data.label,
              street: "",
              zip_code: "",
              city: "",
              Phone: "",
              email: "",
              vat: "",
              employees: "",
              notes: "",
            }
          }
          setShowAddressCard={setShowAddressCard}
          handleAddUpdateAddress={handleAddUpdateAddress}
          handleDeleteAddress={handleDeleteAddress}
        />
      )}

      {/* Risk Dialog */}
      {showRiskDialog && (
        <RiskDialog
          visible={showRiskDialog}
          riskData={
            data.risk || {
              name: data.label,
              description: "",
              category: "",
              impact: "Medium",
              likelihood: "Medium",
              severity: "Medium",
              mitigation: "",
              owner: "",
              status: "Open",
            }
          }
          setShowRiskDialog={setShowRiskDialog}
          handleSaveRisk={handleRiskSave}
          handleDeleteRisk={data.risk ? handleRiskDelete : undefined}
        />
      )}

      {/* Decision Dialog */}
      {showDecisionDialog && (
        <DecisionDialog
          visible={showDecisionDialog}
          decisionData={
            data.decision || {
              name: data.label,
              description: "",
              category: "",
              status: "Pending",
              decisionMaker: "",
              stakeholders: "",
              criteria: "",
              alternatives: "",
              selectedOption: "",
              rationale: "",
              implications: "",
            }
          }
          setShowDecisionDialog={setShowDecisionDialog}
          handleSaveDecision={handleDecisionSave}
          handleDeleteDecision={
            data.decision ? handleDecisionDelete : undefined
          }
        />
      )}

      {/* Image URL Dialog */}
      <Dialog
        visible={showImageDialog}
        modal
        header="Set Image URL"
        style={{ width: "500px", maxWidth: "90vw" }}
        onHide={() => {
          setShowImageDialog(false);
          setImageUrl("");
        }}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              className="p-button-text"
              onClick={() => {
                setShowImageDialog(false);
                setImageUrl("");
              }}
            />
            <Button
              label="Save"
              icon="pi pi-check"
              className="p-button-primary"
              onClick={handleImageUrlSave}
            />
          </div>
        }
      >
        <div style={{ padding: "1rem" }}>
          <label
            htmlFor="imageUrl"
            style={{ display: "block", marginBottom: "0.5rem" }}
          >
            Image URL:
          </label>
          <InputText
            id="imageUrl"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://example.com/image.jpg"
            style={{ width: "100%" }}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleImageUrlSave();
              }
            }}
          />
        </div>
      </Dialog>

      {/* Image Selection Menu */}
      <Menu ref={imageMenuRef} model={imageMenuItems} popup />

      {/* Unsplash Picker Dialog - Disabled due to props mismatch */}
      {/* <Dialog
        visible={showUnsplashPicker}
        onHide={() => setShowUnsplashPicker(false)}
        header="Select Image from Unsplash"
        style={{ width: "80vw", maxWidth: "800px" }}
        modal
      >
        <UnsplashImagePicker
          imagePickerVisible={showUnsplashPicker}
          selectedNode={{ id, data }}
          nodes={nodes}
          setNodes={() => {}}
          setIsDirty={() => {}}
          setImagePickerVisible={setShowUnsplashPicker}
        />
      </Dialog> */}

      {/* Emoji Picker Dialog - Disabled due to props mismatch */}
      {/* <Dialog
        visible={showEmojiPicker}
        onHide={() => setShowEmojiPicker(false)}
        header="Select Emoji"
        style={{ width: "400px" }}
        modal
      >
        <EmojiPickerPopup
          position={{ x: 0, y: 0 }}
          isDragging={false}
          onMouseDown={() => {}}
          onClose={() => setShowEmojiPicker(false)}
          onEmojiSelect={(emoji) => {
            updateNodeData(id, { icon: emoji });
            setShowEmojiPicker(false);
          }}
        />
      </Dialog> */}
    </>
  );
}

export default NodeItem;
